import { EventI } from "../../pojo/EventI";
import { DataAccess } from "../../dataaccess/data.access";
import { ImportBudget } from "./import.budget";
import { ImportTelemetry } from "./import.telemetry";

export interface EventImportRequestDto {
    overrideFolderId?: number;
    overrideCabinetId?: number;
    fallbackFolderId: number;
    fallbackCabinetId: number;
    allowShared: boolean;
    allowBlackoutOverride: boolean;
    allowClosedHoursOverride: boolean;
    moveConflictsToPreferences?: boolean;
    events: EventI[];
}

export interface EventImportSaveResponseDto {
    success?: boolean;
    error?: string;
    reservationSaveResponse?: ReservationSaveResponse;
    fallbackEventIds?: number[];
}

export interface ReservationSaveResponse {
    hasConflict?: boolean;
    success?: boolean;
    error?: string;
    conflicts?: Conflicts;
}

export interface Conflicts {
    spConflicts?: SpConflict[];
    rsConflicts?: RsConflict[];
}

export interface SpConflict {
    roomId?: number;
    origRoomId?: number;
    relnType?: string;
    sharedUse?: boolean;
    conflictType?: ConflictType;
    candidateRsrvStartDt?: string; // LocalDateTime → ISO string
    candidateRsrvEndDt?: string;
    candidateEventId?: number;
    candidateEvDtProfileId?: number;
    candidateRsrvId?: number;
    conflictRsrvStartDt?: string;
    conflictRsrvEndDt?: string;
    conflictEventId?: number;
    conflictEvDtProfileId?: number;
    conflictRsrvId?: number;

    roomName?: string; // front-end only
    conflictTypeName?: string; // front-end only
    candidateDateTimeStr?: string; // front-end only
    conflictDateTimeStr?: string; // front-end only
    candidateEventName?: string; // front-end only
    conflictEventName?: string; // front-end only
}

export interface RsConflict {
    resourceId?: number;
    origResourceId?: number;
    relnType?: string;
    conflictType?: ConflictType;
    candidateEventId?: number;
    candidateRsrvId?: number;
    candidateRsrvStartDt?: string;
    candidateRsrvEndDt?: string;
    conflictEventId?: number;
    conflictRsrvId?: number;
    conflictRsrvStartDt?: string;
    conflictRsrvEndDt?: string;
    stockLevel?: number;
    reservedTotal?: number;
    overBy?: number;

    resourceName?: string; // front-end only
    conflictTypeName?: string; // front-end only
    candidateDateTimeStr?: string; // front-end only
    conflictDateTimeStr?: string; // front-end only
    candidateEventName?: string; // front-end only
    conflictEventName?: string; // front-end only
}

export type ConflictType = "rsrv" | "pending" | "blackout" | "hours" | "stock";

export interface OrganizationImportRequestDto {
    organizations: Organization[];
    isStudentEngagement?: boolean;
}

export interface OrganizationMembership {
    // acctId may NOT be present - it will be set from parent org after org is created/updated
    acctId?: number;
    acctName?: string; // Used by frontend to resolve to acctId (fallback)
    contactEmail?: string; // REQUIRED: Used by backend to create or find contact
    contactFirstName?: string; // REQUIRED: Used by backend when creating contact
    contactFamilyName?: string; // REQUIRED: Used by backend when creating contact
    contId?: number; // Set by backend after contact is created/found
    roleName?: string; // REQUIRED: Used by backend to create or find role
    contRoleId?: number; // Set by backend after role is created/found
}

export interface OrganizationImportSaveResponseDto {
    success?: boolean;
    error?: string;
    organizations?: Organization[];
    customAttributeValuesWritten?: number;
    createdCustomAttributes?: string[];
    rowErrors?: string[];
}

export interface OrganizationAddress {
    addrType: number; // 1 = Administrative, 2 = Billing
    address?: string;
    city?: string;
    stateProv?: string;
    zipPostCode?: string;
    country?: string;
    phone?: string;
    fax?: string;
}

export interface Organization {
    acctId?: number;
    acctName?: string;
    acctTitle?: string;
    acctNumber?: string;
    contactName?: string;
    s25Name?: string;
    orgTypeId?: number;
    orgTypeName?: string; // Used for name-to-id resolution, not sent to backend
    acctRating?: number;
    ratingName?: string; // Used for name-to-id resolution, not sent to backend
    primeContact?: number;
    primeContactEmail?: string; // Used for email-to-id resolution, not sent to backend
    categories?: Array<{ category_id?: number; category_name?: string }>;
    categoryNames?: string[]; // Used for name-to-id resolution, not sent to backend
    lastInvoiceDt?: string;
    lastPaymentDt?: string;
    lastModDt?: string;
    lastModUser?: string;
    uVersion?: string;
    memberships?: OrganizationMembership[];
    addresses?: OrganizationAddress[];
    customAttributes?: ImportCustomAttribute[];
}

export interface ImportCustomAttribute {
    name: string;
    dataType?: string; // optional single-char/numeric type code; required only to auto-create
    value?: string;
}

export interface ContactImportRow {
    email?: string;
    firstName?: string;
    familyName?: string;
    additionalNames?: string;
    namePrefix?: string;
    nameSuffix?: string;
    internalId?: string;
    contTitle?: string;
    address?: string;
    city?: string;
    stateProv?: string;
    zipPostCode?: string;
    country?: string;
    phone?: string;
    fax?: string;
    contId?: number;
    r25Username?: string;
    r25Password?: string; // plaintext from the sheet; encoded server-side
    securityGroupName?: string; // resolved to securityGroupId on the frontend before POST
    securityGroupId?: number;
    customAttributes?: ImportCustomAttribute[];
}

export interface ContactImportRequestDto {
    contacts: ContactImportRow[];
    isStudentEngagement?: boolean;
}

export interface ContactImportSaveResponseDto {
    success?: boolean;
    error?: string;
    createdContacts?: number;
    updatedContacts?: number;
    createdUsers?: number;
    updatedUsers?: number;
    securityGroupsAssigned?: number;
    customAttributeValuesWritten?: number;
    createdCustomAttributes?: string[];
    rowErrors?: string[];
}

export interface MasterDefinitionImportItem {
    id?: number;
    name?: string;
    active?: boolean;
    dataType?: string;
    multiVal?: boolean;
    allowComment?: boolean;
    allowQty?: boolean;
    schedule25?: boolean;
    rateGroupId?: number;
    rateGroupName?: string;
    organizationCode?: string;
    nodeType?: string;
    parentId?: number;
    parentName?: string;
}

export enum MasterDefinitionImportKind {
    EVENT_CATEGORY = "EVENT_CATEGORY",
    ORG_CATEGORY = "ORG_CATEGORY",
    LOCATION_CATEGORY = "LOCATION_CATEGORY",
    RESOURCE_CATEGORY = "RESOURCE_CATEGORY",
    EVENT_CUSTOM_ATTRIBUTE = "EVENT_CUSTOM_ATTRIBUTE",
    ORG_CUSTOM_ATTRIBUTE = "ORG_CUSTOM_ATTRIBUTE",
    CONTACT_CUSTOM_ATTRIBUTE = "CONTACT_CUSTOM_ATTRIBUTE",
    LOCATION_CUSTOM_ATTRIBUTE = "LOCATION_CUSTOM_ATTRIBUTE",
    RESOURCE_CUSTOM_ATTRIBUTE = "RESOURCE_CUSTOM_ATTRIBUTE",
    OTHER_REQUIREMENT = "OTHER_REQUIREMENT",
    CALENDAR_REQUIREMENT = "CALENDAR_REQUIREMENT",
    EVENT_CONTACT_ROLE = "EVENT_CONTACT_ROLE",
    ORG_CONTACT_ROLE = "ORG_CONTACT_ROLE",
    ORG_TYPE = "ORG_TYPE",
    ORG_RATING = "ORG_RATING",
    FEATURE = "FEATURE",
    LAYOUT = "LAYOUT",
    PARTITION = "PARTITION",
    RATE_GROUP = "RATE_GROUP",
    EVENT_TYPE = "EVENT_TYPE",
}

export interface MasterDefinitionImportRequestDto {
    type: MasterDefinitionImportKind;
    items: MasterDefinitionImportItem[];
}

export interface MasterDefinitionImportSaveResponseDto {
    success?: boolean;
    error?: string;
    created?: number;
    updated?: number;
    rowErrors?: string[];
}

export interface SpecialDateImportRow {
    name: string;
    categoryCode: string;
    startDate: string;
    endDate: string;
    showOnCalendar?: boolean;
    active?: boolean;
    seedKey?: string;
}

export interface SpecialDateImportRequestDto {
    dates: SpecialDateImportRow[];
}

export interface SpecialDateImportSaveResponseDto {
    success?: boolean;
    error?: string;
    created?: number;
    updated?: number;
    skipped?: number;
    rowErrors?: string[];
}

export class ImportService {
    public static async importEvents(eventImportRequestDto: EventImportRequestDto) {
        ImportTelemetry.createSession("ImportEvents").complete();
        return DataAccess.post<EventImportSaveResponseDto>("/import/events.json", eventImportRequestDto);
    }

    public static async importOrganizations(organizationImportRequestDto: OrganizationImportRequestDto) {
        ImportTelemetry.createSession("ImportOrganizations").complete();
        return DataAccess.post<OrganizationImportSaveResponseDto>(
            "/import/organizations.json",
            organizationImportRequestDto,
        );
    }

    public static async importContacts(contactImportRequestDto: ContactImportRequestDto) {
        ImportTelemetry.createSession("ImportContacts").complete();
        return DataAccess.post<ContactImportSaveResponseDto>("/import/contacts.json", contactImportRequestDto);
    }

    public static async importMasterDefinitions(request: MasterDefinitionImportRequestDto) {
        ImportTelemetry.createSession("ImportMasterDefinitions").complete();
        return DataAccess.post<MasterDefinitionImportSaveResponseDto>("/import/master-definitions.json", request);
    }

    public static async importBudgets(budgetImportRequestDto: ImportBudget.RequestDto) {
        ImportTelemetry.createSession("ImportBudgets").complete();
        return DataAccess.post<ImportBudget.SaveResponseDto>("/import/budgets.json", budgetImportRequestDto);
    }

    public static async importSpecialDates(specialDateImportRequestDto: SpecialDateImportRequestDto) {
        ImportTelemetry.createSession("ImportSpecialDates").complete();
        return DataAccess.post<SpecialDateImportSaveResponseDto>(
            "/import/special-dates.json",
            specialDateImportRequestDto,
        );
    }
}
